import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  ALL_FACES,
  cardCode,
  type AppraisalTarget,
  faceNotation,
  FREE_COLLATERAL,
  isImmune,
  limitsOf,
  opponent,
  parseSquare,
  sameFace,
  sameSquare,
  squareName,
  type Action,
  type Face,
  type ItemSpec,
  type LinkPreview,
  type MatchEvent,
  type MatchView,
  type PlayLinkAction,
  type RoundSummary,
  type ScoreBreakdown,
  type Side,
  type Square,
} from '../../engine';
import { t } from '../../i18n';
import {
  cardLong,
  describePosition,
  itemName,
  itemText,
  omenName,
  describeTurn,
  formatEvent,
  lastTurnOf,
  namesFor,
  pawnRef,
  type Names,
} from '../../app/describe';
import { canGoFullscreen, isFullscreen, toggleFullscreen } from '../../app/fullscreen';
import type { GameHost } from '../../app/matchHost';
import { holdUpdate } from '../../app/pwa';
import type { Settings } from '../../app/settings';
import { sounds } from '../../app/sound';
import { Board, sqKey, type Highlight } from '../components/Board';
import { CardView } from '../components/CardView';
import { Glyph } from '../components/Glyph';
import { HandView, type HandCardInfo } from '../components/HandView';
import { LogPanel } from '../components/LogPanel';
import {
  CurioDialog,
  DRAWING_CURIOS,
  LedgerDialog,
  LedgerPill,
  kitItems,
} from '../components/LedgerStrip';
import { BrokerEyes, Candle, Placard } from '../components/night/Broker';
import { ruleClock } from '../components/night/ruleClock';
import { ItemSheet, type ShowItem } from '../components/night/Item';
import { Ante, Intermission, ItemMoment, StripKit } from '../components/night/TableKit';
import { HorrorAmbience, useHorrors } from '../components/night/Horrors';
import { darkFiles, inTheDark } from '../../engine/ledger/horrors';
import { turnLimitRule } from '../../engine/ledger/state';
import { HORRORS } from '../../engine/ledger/ids';
import { patchScene } from '../scene';
import { TrickDialog } from '../components/TrickDialog';
import {
  ConfirmDialog,
  EpitaphChoiceDialog,
  HandoffOverlay,
  InscriptionDialog,
  MatchEndDialog,
  NoMoveDialog,
  PileDialog,
  PlayerDialog,
  RoundEndDialog,
  StarterDialog,
  type NoMove,
} from '../components/MatchDialogs';
import { Modal } from '../components/Modal';
import { DeckView, PileView } from '../components/PilePanel';
import { PawnFigure } from '../components/PawnFigure';
import { PlayerStrip } from '../components/PlayerStrip';
import { EpitaphPreview, PreviewPanel } from '../components/PreviewPanel';
import { RouteGlyph } from '../components/RouteGlyph';
import { RouteSteps } from '../components/RouteSteps';
import { guideSeen, markGuideSeen } from '../../app/guides';
import { ShareButton, type ShareResult } from '../components/ShareLink';
import { SymbolLegend } from '../components/SymbolLegend';
import { ScoreBurst } from '../components/ScoreBurst';
import { TallyView } from '../components/TallyView';
import { PawnPeek } from '../components/PawnPeek';
import { Toast } from '../components/Toast';
import { useCardFlights } from '../flights';
import { useQuietWhileBusy } from '../useQuietWhileBusy';
import {
  actionForSquare,
  aimLink,
  cardsForMove,
  dropSquares,
  hopAction,
  hopContinues,
  isLinkAction,
  isTrickAction,
  linkStage,
  pawnsForSquare,
  type Aim,
  type LinkSelection,
} from '../linkInput';
import { watchLongPress } from '../longPress';
import { useDragGesture, type DragSource } from '../useDragGesture';

type Selection =
  | { readonly kind: 'idle' }
  /** A link being put together; `goal` is the square a drag aimed it at, if any. */
  | { readonly kind: 'link'; readonly link: LinkSelection; readonly goal?: Square }
  /** A pawn dropped where more than one card could take it: which card makes the move. */
  | {
      readonly kind: 'pickCard';
      readonly pawnId: string;
      readonly goal: Square;
      readonly cardIds: readonly string[];
    }
  | { readonly kind: 'recover' }
  | {
      readonly kind: 'epitaph';
      readonly epitaphId: string;
      /** On a touch screen, the pawn tapped once: its replay is shown, a second tap plays it. */
      readonly pawnId?: string;
    }
  | { readonly kind: 'inspect'; readonly pawnId: string };

type DialogState =
  | { readonly kind: 'pile' }
  | { readonly kind: 'player'; readonly side: Side }
  | { readonly kind: 'menu' }
  | { readonly kind: 'log' }
  | { readonly kind: 'concede' }
  | { readonly kind: 'describe' }
  | { readonly kind: 'symbols' }
  | { readonly kind: 'confirm'; readonly action: Action; readonly text: string }
  | { readonly kind: 'endTurn' }
  | { readonly kind: 'ledger' }
  | { readonly kind: 'curio'; readonly item: ItemSpec }
  /** A thing of the Long Night read from the table: a Superstition, a Curio. */
  | { readonly kind: 'nightItem'; readonly items: readonly ShowItem[]; readonly at: number }
  /** A Trick to play: its chain pawn and target (Long Night). */
  | { readonly kind: 'trick'; readonly cardId: string }
  /** Séance Table: the same replay, ending the turn or keeping the chain open. */
  | {
      readonly kind: 'seance';
      readonly plain: Action;
      readonly chain: Action;
    }
  | null;

const IDLE: Selection = { kind: 'idle' };
const EMPTY: readonly Action[] = [];

/**
 * The turn being built. Links and Epitaph replays are drafted: shown at once and taken back one
 * by one, then sent together when the turn is ended.
 */
interface Turn {
  /** Drafted actions in order. */
  readonly actions: readonly Action[];
  /** Once sent: the action count before the first of them, and the snapshot version then. */
  readonly sentFrom: number | null;
  readonly sentVersion: number;
  /**
   * Action counts in (echoFrom, echoTo] belong to the last turn sent from here: their moves were
   * already shown while drafted, so they neither fly nor sound again.
   */
  readonly echoFrom: number;
  readonly echoTo: number;
  /** Changes with every drafted action or take-back, to animate it once. */
  readonly step: number;
  /** Events of the action just taken back (they play backwards). */
  readonly undone: readonly MatchEvent[] | null;
}

const NO_TURN: Turn = {
  actions: [],
  sentFrom: null,
  sentVersion: 0,
  echoFrom: -1,
  echoTo: -1,
  step: 0,
  undone: null,
};

/** A fresh turn that keeps the counters of the last one. */
const clearTurn = (t0: Turn): Turn => ({
  ...NO_TURN,
  step: t0.step,
  echoFrom: t0.echoFrom,
  echoTo: t0.echoTo,
});

const NO_EVENTS: readonly MatchEvent[] = [];

/** Moves heard while drafted, not again when the turn is sent. */
const DRAFTED: ReadonlySet<MatchEvent['type']> = new Set([
  'LinkPlayed',
  'PawnCaptured',
  'PawnCrowned',
  'PawnPawned',
  'EpitaphPlayed',
]);

/**
 * The sounds of an action's events. Card sounds come with the flights, unless cards do not fly.
 * `echo`: the action was drafted before, and its moves were heard then. `drafted`: the action is
 * being drafted, and its score is heard only when the turn is sent.
 */
function playEventSounds(
  events: readonly MatchEvent[],
  o: { echo: boolean; drafted: boolean; flying: boolean; viewer: Side; humanCount: number },
): void {
  let played = false;
  for (const e of events) {
    if (o.echo && DRAFTED.has(e.type)) {
      played = true;
      continue;
    }
    switch (e.type) {
      case 'LinkPlayed':
        if (!o.flying) sounds.play('place');
        sounds.play('move');
        played = true;
        break;
      case 'CardDrawn':
        if (!o.flying && e.reason !== 'setup') sounds.play('draw');
        break;
      case 'RoundStarted':
        if (e.round > 1) sounds.play('shuffle');
        break;
      case 'PawnCaptured':
        sounds.play('capture');
        break;
      case 'PawnCrowned':
        sounds.play('crown');
        break;
      case 'PawnPawned':
        sounds.play('pawned');
        break;
      case 'EpitaphPlayed':
        sounds.play('epitaph');
        played = true;
        break;
      case 'ChainScored': {
        if (o.drafted) break;
        // At a Long Night table the Tally sounds the count itself, beat by beat, in its own scale
        // (TallyView); Full Circle keeps its chime under the ensō.
        if (e.breakdown.steps) {
          if (e.breakdown.multiplier.fullCircle) sounds.play('fullCircle');
          break;
        }
        const m = e.breakdown.multiplier;
        const p = m.pattern;
        sounds.play(
          m.fullCircle
            ? 'fullCircle'
            : p === 'ladder' || p === 'echo' || p === 'streak'
              ? p
              : p
                ? 'echo'
                : 'score',
        );
        break;
      }
      case 'Passed':
        if (!played && !o.drafted) sounds.play('pass');
        break;
      case 'MatchEnded':
        sounds.play(
          e.result.winner === o.viewer || (e.result.winner && o.humanCount === 2) ? 'win' : 'lose',
        );
        break;
      default:
        break;
    }
  }
}

/** The round, as a word and one dot per round: done rounds colored by who won them. */
function RoundDots({
  round,
  max,
  winners,
}: {
  round: number;
  max: number;
  winners: readonly (Side | null)[];
}) {
  return (
    <span className="bar-round" role="img" aria-label={t('match.round', { round, max })}>
      <span className="bar-round-label" aria-hidden="true">
        {t('bar.round')}
      </span>
      <span className="round-dots" aria-hidden="true">
        {Array.from({ length: max }, (_, i) => {
          const done = i < winners.length;
          const w = winners[i];
          return (
            <span
              key={i}
              className={`round-dot ${done ? `is-done ${w ? `won-${w}` : 'is-tie'}` : ''} ${
                i + 1 === round && !done ? 'is-current' : ''
              }`}
            />
          );
        })}
      </span>
    </span>
  );
}

function Icon({ name }: { name: 'log' | 'more' }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      {name === 'log' ? (
        <path d="M5 7h14M5 12h14M5 17h9" />
      ) : (
        <>
          <circle cx="6" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="18" cy="12" r="1.6" />
        </>
      )}
    </svg>
  );
}

/** The newest line of the log, read out by screen readers even while the log is closed. */
function LogAnnouncer({ events, names }: { events: readonly MatchEvent[]; names: Names }) {
  let latest = '';
  for (let i = events.length - 1; i >= 0 && !latest; i--)
    latest = formatEvent(events[i]!, names) ?? '';
  return (
    <p className="sr-only" aria-live="polite">
      {latest}
    </p>
  );
}

/**
 * Keeps a line-clamped text whole: when it would be cut off, its font steps down (to 10px at
 * most) until it fits. Checked whenever the text changes and when the window is resized.
 */
function useFitText(ref: { current: HTMLElement | null }, text: string): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.fontSize = '';
      let size = parseFloat(getComputedStyle(el).fontSize);
      while (el.scrollHeight > el.clientHeight + 1 && size > 10) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [ref, text]);
}

/**
 * Keeps what to do beside the pile within the table's height (phones): the prompt and the buttons
 * always show; a note or a preview gets the lines left over, or none, so nothing slides under the
 * strip above or over the hand below.
 */
function useFitTalk(ref: { current: HTMLElement | null }, key: string): void {
  useLayoutEffect(() => {
    const talk = ref.current;
    if (!talk) return;
    const fit = () => {
      const note = talk.querySelector<HTMLElement>('.talk-info');
      if (!note) return;
      note.style.removeProperty('display');
      note.style.removeProperty('-webkit-line-clamp');
      note.style.removeProperty('line-clamp');
      const room = talk.parentElement?.clientHeight ?? 0;
      const over = talk.offsetHeight - room;
      if (room === 0 || over <= 1) return;
      const line = parseFloat(getComputedStyle(note).lineHeight) || 16;
      const lines = Math.floor((note.offsetHeight - over) / line);
      if (lines < 1) {
        note.style.display = 'none';
        return;
      }
      note.style.setProperty('-webkit-line-clamp', String(lines));
      note.style.setProperty('line-clamp', String(lines));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [ref, key]);
}

/** A score preview in a few characters, for the prompt beside the pile (phones). */
function scoreLine(b: ScoreBreakdown): string {
  return t('info.formula', { p: b.points.total, m: b.multiplier.total, score: b.score });
}

export interface MatchScreenProps {
  host: GameHost;
  settings: Settings;
  reduceMotion: boolean;
  exitLabel: string;
  onExit: () => void;
  onSaveAndExit?: () => void;
  onRematch?: () => void;
  onSaveReplay?: () => string | null;
  /** Downloads the match; the menu offers it only when given. */
  onExport?: () => void;
  /** Campaign: show pawns used as collateral at this table as marked (§15.4). */
  showMarks?: boolean;
  title?: string;
  /** Tutorial: the coach's panel, beside the board (above it on phones). */
  coach?: ReactNode;
  /** No input at all (a finished tutorial lesson); `lockedPrompt` replaces the prompt. */
  locked?: boolean;
  lockedPrompt?: string;
  /** Whether the menu offers to concede (default true). */
  canConcede?: boolean;
  /** Whether a finished round is summed up in a dialog (default true). */
  showRoundEnd?: boolean;
  /** Online games: connection and opponent status, over the top of the board. */
  banner?: ReactNode;
  /** Makes a link others can open to watch this match (the menu and the end of the match). */
  onShare?: () => Promise<ShareResult>;
  /** Shown under the final scores (online games: the rating change). */
  endExtra?: ReactNode;
  /**
   * Why the latest save failed (a campaign table, a duel), or null once one works again: shown
   * over the board until closed, and again should saving fail after working.
   */
  saveWarning?: string | null;
  /**
   * Long Night: what the Tally needs from the run (docs/LONG_NIGHT_ART_DIRECTION.md §11.7): the
   * Night, the run's best chain (the record), the Broker's side (his count is written in ink),
   * and a regular's color for their sparkles.
   */
  nightTally?: {
    readonly night: number;
    readonly bestChain: number;
    readonly ink: Side | null;
    readonly tint?: string;
    /** The opponent's one prop, on the House Rule's placard (§12.11). */
    readonly prop?: string;
    /** The opponent's kit is the Broker's own, kept under glass (§11.6). */
    readonly glass?: boolean;
    /** Each pawn's seams of gold and Hallmark (§12.10). */
    readonly marks?: Readonly<
      Record<string, { readonly seams: number; readonly hallmark: boolean }>
    >;
  };
}

export function MatchScreen({
  host,
  settings,
  reduceMotion,
  exitLabel,
  onExit,
  onSaveAndExit,
  onRematch,
  onSaveReplay,
  onExport,
  showMarks = false,
  title,
  nightTally,
  coach,
  locked = false,
  lockedPrompt,
  canConcede = true,
  showRoundEnd = true,
  banner,
  onShare,
  endExtra,
  saveWarning = null,
}: MatchScreenProps) {
  const snap = useSyncExternalStore(host.subscribe, host.getSnapshot);
  const viewer = snap.viewer;

  const [turn, setTurn] = useState<Turn>(NO_TURN);
  const sending = turn.sentFrom !== null;
  // The sent turn has landed, or the table stopped short of it (refused, or the connection
  // dropped): the draft is done with, and what landed is the match.
  if (turn.sentFrom !== null && snap.version > turn.sentVersion && !snap.busy)
    setTurn({ ...clearTurn(turn), echoTo: Math.min(turn.echoTo, snap.actionCount) });
  // While a turn is on its way (online), what is left of it is still shown drafted.
  const pending = useMemo(
    () =>
      turn.sentFrom === null
        ? turn.actions
        : turn.actions.slice(Math.max(0, snap.actionCount - turn.sentFrom)),
    [turn.actions, turn.sentFrom, snap.actionCount],
  );
  const drafted = useMemo(
    () => (pending.length > 0 ? host.draft(pending) : null),
    // The draft is rebuilt whenever the real position moves on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [host, pending, snap.actionCount],
  );
  // A draft the position no longer allows (the match moved on): start the turn again.
  if (drafted && 'error' in drafted && !sending) setTurn(clearTurn(turn));
  const draft = drafted && !('error' in drafted) ? drafted : null;
  const drafting = turn.actions.length > 0;
  // A new release waits for a turn being put together, and for a turn being sent until the table
  // has taken all of it (src/app/pwa.ts). One hold for the whole time: let go in between (as when
  // the drafted turn starts being sent), a waiting release would reload with actions unsent.
  const holdingTurn = drafting || sending || snap.busy;
  useEffect(() => {
    if (!holdingTurn) return;
    holdUpdate('turn', true);
    return () => holdUpdate('turn', false);
  }, [holdingTurn]);
  // What the screen shows: the draft, or the real match.
  const v = draft?.view ?? snap.view;
  /** A Curio's kind by its uid (Long Night). */
  const curioIdOf = (uid: string): string =>
    v.ledger?.curios[viewer].find((c) => c.uid === uid)?.id ?? '';
  const curioItemOf = (uid: string): ItemSpec =>
    v.ledger?.curios[viewer].find((c) => c.uid === uid) ?? { uid, id: '' };
  const names = useMemo(() => namesFor(v, snap.controllers), [v, snap.controllers]);
  const marked = useMemo(
    () => (showMarks ? new Set<string>(v.players.S.collateralUsed) : undefined),
    [showMarks, v.players.S.collateralUsed],
  );
  // The first time a pawn gets a ticket, say what it means (in this match, until dismissed).
  const [ticketTipClosed, setTicketTipClosed] = useState(() => guideSeen('ticket'));
  const ticketTip = (marked?.size ?? 0) > 0 && !ticketTipClosed;
  // The first Long Night table says where its things are (once, until dismissed).
  const [ledgerTipClosed, setLedgerTipClosed] = useState(() => guideSeen('night-table'));
  const ledgerTip = !!v.ledger && !ledgerTipClosed && !ticketTip && !locked;
  useEffect(() => {
    if (ticketTip) markGuideSeen('ticket');
  }, [ticketTip]);
  // A chain's score being counted (ScoreBurst): the scorer's round score beside the board shows
  // what it was before (`held`) until the score lands, then rolls up to it (roundShown).
  const [burst, setBurst] = useState<{
    breakdown: ScoreBreakdown;
    side: Side;
    source: 'chain' | 'epitaph';
    pawnId: string;
    key: number;
    held: number;
    landed: boolean;
    /** Long Night: the chain wins the round on its own; the run's best chain so far. */
    flare: boolean;
    record: boolean;
  } | null>(null);
  // While a score is counted the turn is still the scorer's: nobody acts until it lands.
  const countingNow = burst !== null && !burst.landed;
  const humanCount = (['S', 'N'] as const).filter((s) => host.isHuman(s)).length;
  // The real match waits for this device's player.
  const canAct =
    !locked &&
    !countingNow &&
    snap.view.phase !== 'MATCH_OVER' &&
    snap.view.decider === viewer &&
    host.isHuman(viewer) &&
    !snap.handoff &&
    snap.thinking === null;
  // A turn that starts with nothing to draw (a full hand, as at the start of a round, or an empty
  // deck) has nothing to wait for: its moves are taken at once, as if Continue had been pressed,
  // and the first one carries that Continue with it, drafted, so an Undo goes back to where Pass
  // is still open. A turn that can draw is not: what it draws is seen when the player draws it.
  const startDraft = useMemo(() => {
    const real = snap.view;
    if (!canAct || sending || drafting || real.phase !== 'TURN_START') return null;
    const me0 = real.players[viewer];
    if ((me0.hand?.length ?? 0) < limitsOf(real, viewer).hand && me0.deckCount > 0) return null;
    const d = host.draft([{ type: 'CONTINUE', side: viewer }]);
    return 'error' in d ? null : d;
  }, [host, snap.view, canAct, sending, drafting, viewer]);
  const quietStart =
    startDraft && startDraft.view.phase === 'ACTION' && startDraft.view.decider === viewer
      ? startDraft
      : null;
  // Or nothing can be done at all (no card moves, no route can be replayed, nothing to draw or
  // Recover): Continue would only pass, so the turn says so and offers Pass alone.
  const stuckStart =
    startDraft?.events[0]?.some((e) => e.type === 'Passed' && e.side === viewer) ?? false;
  // What answers questions about the position (moves, previews): the draft, the turn started
  // quietly, or the real match.
  const table = draft ?? quietStart ?? host;
  // Board and hand take input while the shown position is still this player's to play.
  const myTurn =
    canAct &&
    !sending &&
    v.decider === viewer &&
    (!drafting || v.phase === 'ACTION' || v.phase === 'CHAIN');
  const legal = myTurn ? table.legal(viewer) : EMPTY;
  // Moves can be made: a turn under way, or one started quietly.
  const acting = myTurn && (v.phase === 'ACTION' || v.phase === 'CHAIN' || quietStart !== null);
  // A drafted turn, or a chain left open (a match resumed mid-chain), is ended with one button.
  const canEndTurn = canAct && !sending && (drafting || snap.view.phase === 'CHAIN');
  // The drafted turn has nothing left to play (its chain ended on its own): only End turn remains.
  const turnSpent = canEndTurn && drafting && !acting;
  // A Brazier lit under this player's chain pawn (LEDGER.MD §6): any card goes next, and burns.
  const brazierLit = (() => {
    const ch = v.chain;
    if (!ch || ch.side !== viewer) return false;
    const sq = v.pawns[ch.pawnId]?.square;
    const last = ch.landings[ch.landings.length - 1];
    return (
      !!sq &&
      !!last &&
      sameSquare(sq, last) &&
      !!v.ledger?.omens.some((o) => o.kind === 'brazier' && !o.spent && sameSquare(o.square, sq))
    );
  })();

  const [sel, setSel] = useState<Selection>(IDLE);
  // A pawn held down (long press, right click, I): its Inscription is shown over the board,
  // whatever is being done meanwhile (PawnPeek).
  const [peek, setPeek] = useState<string | null>(null);
  // A mouse can point at a pawn to preview its Epitaph before a click plays it; a touch screen
  // has no hover, so there the first tap shows the replay and a second one plays it.
  const [pointsFirst] = useState(
    () => !!globalThis.matchMedia?.('(hover: hover) and (pointer: fine)').matches,
  );
  const stopLongPress = useRef<(() => void) | null>(null);
  useEffect(() => () => stopLongPress.current?.(), []);
  const [pointerSq, setPointerSq] = useState<Square | null>(null);
  const [focusSq, setFocusSq] = useState<Square | null>(null);
  const [lastInput, setLastInput] = useState<'pointer' | 'focus'>('pointer');
  // The square being previewed: the most recent of mouse hover and keyboard focus.
  const hover = lastInput === 'focus' ? (focusSq ?? pointerSq) : (pointerSq ?? focusSq);
  const onPointerSquare = useCallback((sq: Square | null, moved: boolean) => {
    setPointerSq((prev) => (sq && prev && sameSquare(prev, sq) ? prev : sq));
    // Only real pointer movement takes over from keyboard focus (not hover changes from scrolling).
    if (sq && moved) setLastInput('pointer');
  }, []);
  const onFocusSquare = useCallback((sq: Square | null) => {
    setFocusSq(sq);
    if (sq) setLastInput('focus');
  }, []);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [roundDialog, setRoundDialog] = useState<RoundSummary | null>(null);
  // A player the game passed for, with nothing left to play: told before what follows.
  const [noMove, setNoMove] = useState<NoMove | null>(null);
  // The round's end is being told: the next round waits.
  const roundClosing = roundDialog !== null || noMove !== null;
  // Closing Time, on the player's turn: the smoke holds still (§11.17).
  const atNight = !!nightTally;
  const holdSmoke =
    !!nightTally?.ink &&
    snap.view.phase !== 'MATCH_OVER' &&
    snap.view.decider === viewer &&
    snap.thinking === null;
  useEffect(() => {
    if (!atNight) return;
    patchScene({ held: holdSmoke });
    return () => patchScene({ held: false });
  }, [atNight, holdSmoke]);
  // A Superstition taken or foreclosed at the table (Long Night, §11.17).
  const [itemMoment, setItemMoment] = useState<{
    reason: 'stolen' | 'foreclosed';
    id: string;
    key: number;
  } | null>(null);
  // The best chain at this table so far, for the Tally's record.
  const [bestHere, setBestHere] = useState(0);
  // A message over the board: at its foot, or over its top (the pawn question, kept off the pawns).
  const [notice, setNotice] = useState<{ text: string; top: boolean } | null>(null);
  const say = (text: string | null, top = false) => setNotice(text === null ? null : { text, top });
  const [flipped, setFlipped] = useState(false);
  // A save warning closed by the player comes back only once saving has worked and failed again.
  const [warningClosed, setWarningClosed] = useState(false);
  if (warningClosed && !saveWarning) setWarningClosed(false);
  const warning = warningClosed ? null : saveWarning;

  // Every new state (or a new viewer in two-player games) starts a fresh selection, and the
  // latest action's visual cues (score burst, round summary, pass explanations) are derived once.
  const stateKey = `${snap.actionCount}:${viewer}`;
  const [seenKey, setSeenKey] = useState(stateKey);
  if (seenKey !== stateKey) {
    const newAction = seenKey.split(':')[0] !== String(snap.actionCount);
    setSeenKey(stateKey);
    setSel(IDLE);
    setPeek(null);
    setPointerSq(null);
    // The match moved on without this turn (another viewer, a new position from the table).
    if (!sending && (drafting || turn.undone)) setTurn(clearTurn(turn));
    if (newAction) {
      const events = snap.lastEvents;
      for (const e of events) {
        if (e.type === 'ChainScored') {
          const held = e.roundScore - e.breakdown.score;
          // The flare: one chain beats everything the other side leads by, head start included.
          const lead = snap.view.players[opponent(e.side)].roundScore - held;
          const best = Math.max(nightTally?.bestChain ?? Infinity, bestHere);
          const record =
            !!nightTally && nightTally.night >= 2 && e.side === 'S' && e.breakdown.score > best;
          if (e.side === 'S') setBestHere((b) => Math.max(b, e.breakdown.score));
          setBurst({
            breakdown: e.breakdown,
            side: e.side,
            source: e.source,
            pawnId: e.pawnId,
            key: snap.actionCount,
            held,
            landed: false,
            flare: !!v.ledger && lead > 0 && e.breakdown.score > lead,
            record,
          });
        } else if (
          e.type === 'ItemMoved' &&
          nightTally &&
          e.from === viewer &&
          (e.reason === 'stolen' || e.reason === 'foreclosed')
        ) {
          setItemMoment({ reason: e.reason, id: e.id, key: snap.actionCount });
        } else if (e.type === 'Passed' && e.kind !== 'voluntary') {
          // Told in full when it ends the round, or when it is this device's player: no round
          // ends, and no one is put out of one, without seeing why.
          const ended = events.find((x) => x.type === 'RoundEnded');
          if (showRoundEnd && (ended || (host.isHuman(e.side) && e.side === viewer))) {
            const drawn = events.find(
              (x) => x.type === 'CardDrawn' && x.side === e.side && x.reason === 'draw',
            );
            setNoMove({
              side: e.side,
              kind: e.kind,
              ...(e.limit
                ? { limit: { rule: itemName(`rule.${e.limit.rule}`), n: e.limit.n } }
                : {}),
              drew: drawn?.type === 'CardDrawn' ? drawn.card : undefined,
              roundOver: ended?.type === 'RoundEnded' ? ended.round : null,
              matchOver: events.some((x) => x.type === 'MatchEnded'),
              ...(ended?.type === 'RoundEnded'
                ? {
                    roundResult: ended.winner
                      ? t('dialog.roundEnd.winner', {
                          name: names.side(ended.winner),
                          w: ended.scores[ended.winner],
                          l: ended.scores[opponent(ended.winner)],
                        })
                      : t('dialog.roundEnd.tie', { s: ended.scores.S }),
                  }
                : {}),
            });
          } else
            say(
              e.kind === 'outOfTurns' && e.limit
                ? t('explain.outOfTurns', {
                    name: names.side(e.side),
                    n: e.limit.n,
                    rule: itemName(`rule.${e.limit.rule}`),
                  })
                : t(e.kind === 'automatic' ? 'explain.automaticPass' : 'explain.forcedPass', {
                    name: names.side(e.side),
                  }),
            );
        } else if (
          e.type === 'RoundEnded' &&
          showRoundEnd &&
          !events.some((x) => x.type === 'MatchEnded')
        )
          setRoundDialog({
            round: e.round,
            starter: v.roundStarter,
            scores: e.scores,
            winner: e.winner,
          });
      }
      // Short Wick, the Clock: the last turn the rule allows is announced as it starts.
      const lim = snap.view.ledger ? turnLimitRule(snap.view, viewer) : null;
      if (
        lim &&
        host.isHuman(viewer) &&
        events.some((e) => e.type === 'TurnStarted' && e.side === viewer) &&
        !snap.view.players[viewer].passed &&
        snap.view.ledger?.round.turns[viewer] === lim.n
      )
        say(t('notice.lastTurn', { rule: itemName(`rule.${lim.rule}`), n: lim.n }));
    }
  }

  // Sounds for the latest action, played once per action, and for each drafted action.
  const soundedRef = useRef(snap.actionCount);
  const flying = !reduceMotion && !snap.handoff;
  const echo = snap.actionCount > turn.echoFrom && snap.actionCount <= turn.echoTo;
  useEffect(() => {
    if (soundedRef.current === snap.actionCount) return;
    soundedRef.current = snap.actionCount;
    sounds.enabled = settings.sound;
    sounds.volume = settings.volume;
    playEventSounds(snap.lastEvents, { echo, drafted: false, flying, viewer, humanCount });
  }, [
    snap.actionCount,
    snap.lastEvents,
    settings.sound,
    settings.volume,
    echo,
    flying,
    viewer,
    humanCount,
  ]);
  const draftSoundedRef = useRef(turn.step);
  useEffect(() => {
    if (draftSoundedRef.current === turn.step) return;
    draftSoundedRef.current = turn.step;
    if (turn.undone || !draft) return;
    sounds.enabled = settings.sound;
    sounds.volume = settings.volume;
    playEventSounds(draft.events.at(-1) ?? [], {
      echo: false,
      drafted: true,
      flying,
      viewer,
      humanCount,
    });
  }, [turn.step, turn.undone, draft, settings.sound, settings.volume, flying, viewer, humanCount]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4500);
    return () => clearTimeout(timer);
  }, [notice]);

  // The game waits while a score is counted, so that the next move never hides it.
  const counting = burst !== null;

  // The Epitaph keep choice comes on the opponent's turn, straight after the move that captured
  // the pawns: it opens once that move has been seen (its cards flown, its captures shown).
  const choosingEpitaph =
    !roundClosing &&
    canAct &&
    snap.view.phase === 'CHOOSE_EPITAPH' &&
    snap.view.pendingEpitaphChoice !== null;
  const [epitaphChoiceAt, setEpitaphChoiceAt] = useState<number | null>(null);
  useEffect(() => {
    if (!choosingEpitaph) return;
    const wait = reduceMotion || snap.lastActor === viewer ? 0 : 1200;
    const timer = setTimeout(() => setEpitaphChoiceAt(snap.actionCount), wait);
    return () => clearTimeout(timer);
  }, [choosingEpitaph, snap.actionCount, snap.lastActor, viewer, reduceMotion]);
  const epitaphChoiceShown = choosingEpitaph && epitaphChoiceAt === snap.actionCount;

  const orientation: Side = (() => {
    const base = humanCount === 2 && !settings.flipHotseat ? 'S' : viewer;
    return flipped ? opponent(base) : base;
  })();
  const flipRoute = orientation === 'N';
  // After Night III: the boss's powers, shown happening (night/Horrors.tsx); the game waits.
  const horrors = useHorrors(
    snap.lastEvents,
    snap.actionCount,
    orientation,
    reduceMotion,
    !counting && !roundClosing,
  );
  const horrorRule = snap.view.config.ledger?.houseRules.find((r) =>
    (HORRORS as readonly string[]).includes(r.id),
  )?.id;
  useEffect(() => {
    host.setPaused(locked || roundClosing || counting || horrors.busy);
  }, [host, locked, roundClosing, counting, horrors.busy]);
  /** The pawn question goes over the top of the board, unless a pawn it asks about is up there. */
  const pawnQuestionAtTop = (ids: readonly string[]) =>
    !ids.some((id) => {
      const y = v.pawns[id]?.square?.y;
      return y !== undefined && (orientation === 'S' ? y >= 3 : y <= 2);
    });

  // Cards fly between the deck, the hand and the pile.
  const rootRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const handShown = !snap.handoff && v.players[viewer].hand !== null;
  const draftStepEvents = turn.undone ?? draft?.events.at(-1) ?? [];
  const shownStep = `${snap.actionCount}.${turn.step}`;
  // What the board plays (landings, captures, a Looking Glass…): each step as it is shown, a
  // drafted move when it is made, not again when the turn is sent, and backwards when a move is
  // taken back (through its Looking Glass again, its Brazier flaring).
  const boardEvents = echo ? NO_EVENTS : (turn.undone ?? draft?.events.at(-1) ?? snap.lastEvents);
  const boardKey = snap.actionCount * 1000 + turn.step;
  useQuietWhileBusy(rootRef, shownStep);
  // The height the match really has, for the board's size (styles.css, --match-h): the screen the
  // browser leaves, whatever its viewport units count (room under a phone browser's own bars).
  // And whether the game fits it: then there is nothing to scroll to, however far a glow or a
  // fanned card reaches past the edge (data-fits).
  useLayoutEffect(() => {
    const el = rootRef.current;
    const inner = el?.querySelector<HTMLElement>(':scope > .match-inner');
    if (!el || !inner || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      el.style.setProperty('--match-h', `${el.clientHeight}px`);
      el.toggleAttribute('data-fits', inner.offsetHeight <= el.clientHeight);
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    watch.observe(inner);
    return () => watch.disconnect();
  }, []);
  useCardFlights({
    root: rootRef,
    layer: layerRef,
    // A step of what is shown: an action of the match (not one just sent from the draft, which
    // was already shown), or a drafted action or take-back.
    step: shownStep,
    events: echo ? [] : draftStepEvents.length > 0 ? draftStepEvents : snap.lastEvents,
    reverse: !!turn.undone,
    side: handShown ? viewer : null,
    enabled: flying,
    deal: snap.actionCount === 0 && !coach && v.round === 1,
    onSound: (name) => {
      sounds.enabled = settings.sound;
      sounds.volume = settings.volume;
      sounds.play(name);
    },
  });

  const submit = useCallback(
    (a: Action) => {
      const err = host.submit(a);
      if (err) say(t('error.illegal', { message: err.message }));
    },
    [host],
  );
  const quietContinue: Action = { type: 'CONTINUE', side: viewer };

  // Keep or replace an inscription without asking, when Settings say how (issue #10).
  const autoInscribed = useRef(-1);
  useEffect(() => {
    const pending = snap.view.pendingInscription;
    if (
      settings.inscriptions === 'ask' ||
      !canAct ||
      snap.view.phase !== 'CHOOSE_INSCRIPTION' ||
      !pending ||
      autoInscribed.current === snap.actionCount
    )
      return;
    autoInscribed.current = snap.actionCount;
    const old = snap.view.pawns[pending.pawnId]?.inscription;
    const replace =
      settings.inscriptions === 'better' &&
      !!old &&
      pending.inscription.recordedScore > old.recordedScore;
    const text = t(replace ? 'notice.inscriptionReplaced' : 'notice.inscriptionKept', {
      pawn: pawnRef(snap.view, pending.pawnId, names),
      rule: t(`settings.inscriptions.${settings.inscriptions}`),
    });
    submit({ type: 'CHOOSE_INSCRIPTION', side: viewer, replace });
    // Told once the choice is made (the next render), not in the middle of this effect.
    setTimeout(() => say(text), 0);
  }, [snap, canAct, settings.inscriptions, viewer, submit, names]);

  /**
   * Plays an action. Links and Epitaph replays are drafted: they show at once and can be taken
   * back until the turn is ended. A Recover draws a card, so it is played for real at once
   * (after a confirmation, when the player asked for them: §19.14).
   */
  const commit = (a: Action) => {
    setSel(IDLE);
    // A Curio that draws reveals a card: it is played at once (only offered before any move is
    // drafted). The others are drafted with the turn, like the moves around them; at the start of
    // a turn that can still draw, they are played at once too.
    const drafts =
      a.type === 'PLAY_LINK' ||
      a.type === 'PLAY_EPITAPH' ||
      (a.type === 'USE_CURIO' &&
        !DRAWING_CURIOS.has(curioIdOf(a.uid)) &&
        (v.phase !== 'TURN_START' || quietStart !== null));
    // A Trick that draws (Draw Two) reveals cards: the turn so far is sent with it, at once.
    if (a.type === 'PLAY_LINK' && 'trick' in a.target && v.cards[a.cardId]?.trick === 'draw-two') {
      const start = quietStart && turn.actions.length === 0 ? [quietContinue] : [];
      sendDraft([...turn.actions, ...start, a]);
      return;
    }
    if (drafts) {
      const start = quietStart && turn.actions.length === 0 ? [quietContinue] : [];
      setTurn((t0) => ({
        ...t0,
        actions: [...t0.actions, ...start, a],
        step: t0.step + 1,
        undone: null,
      }));
      return;
    }
    if (settings.confirmActions && a.type === 'RECOVER') {
      setDialog({
        kind: 'confirm',
        action: a,
        text: t('dialog.confirm.recover', { card: cardLong(v.cards[a.cardId]) }),
      });
      return;
    }
    playNow(a);
  };

  /** Plays an action for real at once: with the Continue of a turn started quietly, if any. */
  const playNow = (a: Action) => {
    if (!quietStart) {
      submit(a);
      return;
    }
    const err = host.submitAll([quietContinue, a]);
    if (err) say(t('error.illegal', { message: err.message }));
  };

  /** Takes the last drafted action back (with the Continue that came with the first move). */
  const takeBack = () => {
    if (!drafting || sending) return;
    setSel(IDLE);
    setTurn((t0) => {
      const actions = t0.actions.slice(0, -1);
      return {
        ...t0,
        actions: actions.length === 1 && actions[0]!.type === 'CONTINUE' ? [] : actions,
        step: t0.step + 1,
        undone: draft?.events.at(-1) ?? [],
      };
    });
  };

  /**
   * Sends drafted actions to the table: the whole turn (Ends the turn), or the turn so far with a
   * move that reveals cards (Draw Two), after which the turn goes on.
   */
  const sendDraft = (actions: Action[]) => {
    setSel(IDLE);
    const before = host.getSnapshot();
    const err = host.submitAll(actions);
    const after = host.getSnapshot();
    if (err) {
      say(t('error.illegal', { message: err.message }));
      // Nothing was played: the draft stays, to be sent again. Else what landed is the match.
      if (after.actionCount !== before.actionCount)
        setTurn((t0) => ({
          ...clearTurn(t0),
          echoFrom: before.actionCount,
          echoTo: after.actionCount,
        }));
      return;
    }
    setTurn((t0) => ({
      ...t0,
      actions,
      sentFrom: before.actionCount,
      sentVersion: before.version,
      echoFrom: before.actionCount,
      echoTo: before.actionCount + actions.length,
      undone: null,
    }));
  };

  /** Ends the turn: sends the drafted actions, closing a chain that is still open. */
  const endTurn = (confirmed = false) => {
    if (!canEndTurn) return;
    if (settings.confirmActions && !confirmed) {
      setDialog({ kind: 'endTurn' });
      return;
    }
    const shown = draft?.view ?? snap.view;
    const actions = [...turn.actions];
    if (shown.phase === 'CHAIN' && shown.decider === viewer)
      actions.push({ type: 'END_CHAIN', side: viewer });
    sendDraft(actions);
  };

  // ---- Derived input state -------------------------------------------------------------------
  const chainPawn = v.chain?.pawnId ?? null;
  const linkLegal = useMemo(() => legal.filter(isLinkAction), [legal]);
  const trickLegal = useMemo(() => legal.filter(isTrickAction), [legal]);
  const playableIds = useMemo(
    () => new Set([...linkLegal, ...trickLegal].map((a) => a.cardId)),
    [linkLegal, trickLegal],
  );
  const epitaphActions = useMemo(() => legal.filter((a) => a.type === 'PLAY_EPITAPH'), [legal]);
  const recoverActions = useMemo(() => legal.filter((a) => a.type === 'RECOVER'), [legal]);
  /** Why a Curio that could be used is not offered now: one that draws, once moves are drafted. */
  const curioHint = (item: ItemSpec): string | null =>
    DRAWING_CURIOS.has(item.id) && drafting ? t('ledger.curio.drawsFirst') : null;
  const curioLegal = useMemo(
    () => legal.filter((a): a is Extract<Action, { type: 'USE_CURIO' }> => a.type === 'USE_CURIO'),
    [legal],
  );
  const stage = sel.kind === 'link' ? linkStage(legal, sel.link) : null;
  const epitaphChecks = useMemo(
    () =>
      sel.kind === 'epitaph'
        ? table.epitaphOptions(viewer).filter((o) => o.epitaph.id === sel.epitaphId)
        : [],
    [table, sel, viewer],
  );

  // Before the turn is under way (Draw or Pass), a card that could not be played is already
  // greyed with its reason; one that could stays as it is. A turn that can only pass greys all.
  const startLook = (id: string) => {
    if (!myTurn || v.phase !== 'TURN_START') return null;
    const p = table.playability(viewer, id);
    return p.playable ? null : p;
  };
  const handCards: HandCardInfo[] = (v.players[viewer].hand ?? []).map((id) => ({
    card: v.cards[id]!,
    playability: acting ? table.playability(viewer, id) : startLook(id),
  }));

  const pawnAtSquare = (sq: Square) =>
    Object.values(v.pawns).find(
      (p) => p.status === 'onBoard' && p.square && sameSquare(p.square, sq),
    );
  // The card of a pawn held down goes in the half of the board away from it.
  const peekSquare = peek ? v.pawns[peek]?.square : null;
  const peekPlace: 'top' | 'bottom' =
    peekSquare && (orientation === 'S' ? peekSquare.y >= 3 : peekSquare.y <= 2) ? 'bottom' : 'top';
  // The pawn whose Epitaph replay is shown: the one held down (touch screens have no hover), else
  // the hovered one.
  const epitaphPawnId =
    (sel.kind === 'epitaph' ? sel.pawnId : undefined) ??
    (sel.kind === 'epitaph' && peek ? peek : undefined) ??
    (hover ? pawnAtSquare(hover)?.id : undefined);

  // Action being previewed (pointer and keyboard): the hovered destination, else "stop here".
  let previewAction: PlayLinkAction | null = null;
  if (stage?.stage === 'target') {
    if (hover)
      previewAction = stage.yellow ? hopAction(stage, hover) : actionForSquare(stage, hover);
    if (!previewAction && stage.stopHere) previewAction = stage.stopHere;
  }
  let preview: LinkPreview | null = null;
  if (previewAction && settings.showPreview) {
    const p = table.preview(previewAction);
    if (!('error' in p)) preview = p;
  }

  // ---- Drag and drop ---------------------------------------------------------------------------
  // A pawn or a card can be dragged to a square as well as tapped (§19.5). The drop aims the move
  // at that square (src/ui/linkInput.ts, aimLink): a move it settles is made at once, and whatever
  // it leaves open (which pawn, which card, a Wild's face or collateral) is asked as a tap would.
  const canDrag = acting && !sending && sel.kind !== 'recover' && sel.kind !== 'epitaph';
  const draggablePawns = new Set<string>();
  if (canDrag) {
    if (sel.kind !== 'link') for (const a of linkLegal) draggablePawns.add(a.pawnId as string);
    else if (stage && (stage.stage === 'pawn' || stage.stage === 'target'))
      for (const id of chainPawn ? [chainPawn] : stage.pawnChoices) draggablePawns.add(id);
  }
  const draggableSquares = new Set<string>();
  for (const id of draggablePawns) {
    const sq = v.pawns[id]?.square;
    if (sq) draggableSquares.add(sqKey(sq));
  }

  /** Makes the move an aim settles, or goes on from what it leaves open. */
  const followAim = (aim: Aim | null, goal: Square, otherwise: LinkSelection | null) => {
    if (aim && 'action' in aim) {
      commit(aim.action);
      return;
    }
    if (aim) {
      setSel({ kind: 'link', link: aim.selection, goal });
      if (aim.ask === 'pawn') say(t('prompt.whichPawn'), pawnQuestionAtTop(aim.pawns ?? []));
      return;
    }
    if (otherwise) setSel({ kind: 'link', link: otherwise });
  };

  /** A choice made while a link is put together; after a drop it may settle the move. */
  const chooseLink = (link: LinkSelection) => {
    const goal = sel.kind === 'link' ? sel.goal : undefined;
    const aim = goal ? aimLink(legal, link, goal) : null;
    if (goal && aim) followAim(aim, goal, link);
    else setSel({ kind: 'link', link });
  };

  const beforeDrag = useRef<Selection>(IDLE);
  const drag = useDragGesture({
    onStart: (source) => {
      setNotice(null);
      beforeDrag.current = sel;
      if (source.kind === 'card') {
        // Dragging a card picks it, so its destinations light up.
        if (sel.kind !== 'link' || sel.link.cardId !== source.cardId)
          setSel({
            kind: 'link',
            link: {
              cardId: source.cardId,
              pawnId: chainPawn,
              face: null,
              collateral: null,
              hops: [],
            },
          });
      } else if (sel.kind === 'link') {
        // Another pawn that can play the picked card: its destinations light up instead.
        if (!chainPawn && sel.link.pawnId !== source.pawnId)
          setSel({
            kind: 'link',
            link: { ...sel.link, pawnId: source.pawnId, face: null, collateral: null, hops: [] },
          });
      } else setSel(IDLE);
    },
    onOver: (name) => onPointerSquare(name ? parseSquare(name) : null, true),
    onDrop: (source, name) => {
      onPointerSquare(null, false);
      const goal = name ? parseSquare(name) : null;
      if (source.kind === 'card') {
        // Off the board: nothing happened.
        if (!goal || sel.kind !== 'link') {
          setSel(beforeDrag.current);
          return;
        }
        const aim = aimLink(legal, sel.link, goal);
        if (aim) {
          followAim(aim, goal, null);
          return;
        }
        // Dropped on a pawn that can play the card: that pawn is chosen.
        const pawn = pawnAtSquare(goal);
        if (pawn && !chainPawn && stage?.pawnChoices.includes(pawn.id))
          setSel({
            kind: 'link',
            link: { ...sel.link, pawnId: pawn.id, face: null, collateral: null, hops: [] },
          });
        return;
      }
      if (!goal) return;
      if (sel.kind === 'link') {
        followAim(aimLink(legal, sel.link, goal), goal, null);
        return;
      }
      // No card picked: the card that makes this move. A plain card is used before a Wild; when
      // different cards could make it, the player picks one.
      const ids = cardsForMove(legal, source.pawnId, goal);
      const plain = ids.filter((id) => v.cards[id]?.kind !== 'wild');
      const byCode = new Map<string, string>();
      for (const id of plain.length > 0 ? plain : ids) {
        const card = v.cards[id];
        if (card && !byCode.has(cardCode(card))) byCode.set(cardCode(card), id);
      }
      const cardIds = [...byCode.values()];
      if (cardIds.length === 1) {
        const link = {
          cardId: cardIds[0] as string,
          pawnId: source.pawnId,
          face: null,
          collateral: null,
          hops: [],
        };
        followAim(aimLink(legal, link, goal), goal, link);
      } else if (cardIds.length > 1)
        setSel({ kind: 'pickCard', pawnId: source.pawnId, goal, cardIds });
    },
  });
  const dragging = drag.active?.source ?? null;

  const pawnUnder = (target: EventTarget) => {
    const el = (target as Element).closest<HTMLElement>('[data-square]');
    const sq = el?.dataset.square ? parseSquare(el.dataset.square) : null;
    return { el, pawn: sq ? pawnAtSquare(sq) : undefined };
  };
  const onBoardPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    const { el, pawn } = pawnUnder(e.target);
    // Any pawn held down shows its Inscription, mine or not, my turn or not.
    stopLongPress.current?.();
    stopLongPress.current =
      pawn && e.isPrimary && e.button === 0
        ? watchLongPress(e, () => {
            drag.cancel();
            setPeek(pawn.id);
            navigator.vibrate?.(8);
          })
        : null;
    if (!canDrag) return;
    if (el && pawn && draggablePawns.has(pawn.id))
      drag.begin({ kind: 'pawn', pawnId: pawn.id }, e, el);
  };
  const onBoardContextMenu = (e: ReactMouseEvent<HTMLElement>) => {
    const { pawn } = pawnUnder(e.target);
    if (!pawn) return;
    e.preventDefault();
    setPeek(pawn.id);
  };
  const onHandPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (!canDrag) return;
    const item = (e.target as Element).closest<HTMLElement>('[data-card-id]');
    const cardId = item?.dataset.cardId;
    if (item && cardId && playableIds.has(cardId))
      drag.begin({ kind: 'card', cardId }, e, item.querySelector('.card') ?? item);
  };

  // ---- Board highlights ----------------------------------------------------------------------
  const highlights = new Map<string, Highlight>();
  let ghost: Square[] | null = null;
  let ghostKind: 'preview' | 'epitaph' = 'preview';
  if (stage) {
    const pawnSq = (id: string | null) => (id ? v.pawns[id]?.square : null);
    if (stage.stage === 'pawn') {
      // Every square the card can reach, with any pawn: tapping one moves the pawn that can.
      for (const sq of stage.squares) {
        const occupant = pawnAtSquare(sq);
        highlights.set(
          sqKey(sq),
          stage.yellow ? 'hop' : occupant && occupant.side !== viewer ? 'capture' : 'move',
        );
      }
      for (const id of stage.pawnChoices) {
        const sq = pawnSq(id);
        if (sq) highlights.set(sqKey(sq), 'pawn');
      }
    }
    if (stage.stage === 'collateral')
      for (const id of stage.collateralChoices) {
        const sq = pawnSq(id);
        if (sq) highlights.set(sqKey(sq), 'collateral');
      }
    const selected = pawnSq(stage.selection.pawnId);
    if (selected) highlights.set(sqKey(selected), 'selected');
    if (stage.stage === 'target') {
      for (const sq of stage.squares) {
        // Only an enemy there makes it a capture: an own pawn there is the collateral, pawned first.
        const occupant = pawnAtSquare(sq);
        highlights.set(
          sqKey(sq),
          stage.yellow ? 'hop' : occupant && occupant.side !== viewer ? 'capture' : 'move',
        );
      }
      if (selected && stage.selection.hops.length > 0) ghost = [selected, ...stage.selection.hops];
    }
    if (preview && selected) ghost = [selected, ...preview.step.landings];
  } else if (sel.kind === 'epitaph') {
    ghostKind = 'epitaph';
    for (const o of epitaphChecks) {
      const sq = v.pawns[o.pawnId]?.square;
      if (sq && o.check.ok) highlights.set(sqKey(sq), 'epitaph');
    }
    // The pawn tapped once on a touch screen: its replay is the one shown.
    const picked = sel.pawnId ? v.pawns[sel.pawnId]?.square : null;
    if (picked) highlights.set(sqKey(picked), 'selected');
    const shown = epitaphPawnId ? v.pawns[epitaphPawnId] : undefined;
    const check = epitaphChecks.find((o) => o.pawnId === shown?.id)?.check;
    // Through a Looking Glass the route goes on from the one opposite.
    if (check?.ok && shown?.square)
      ghost = [
        shown.square,
        ...check.steps.flatMap((s) =>
          s.glass ? [...s.option.landings, s.glass] : s.option.landings,
        ),
      ];
  } else if (sel.kind === 'pickCard') {
    const from = v.pawns[sel.pawnId]?.square;
    if (from) highlights.set(sqKey(from), 'selected');
    highlights.set(sqKey(sel.goal), 'move');
  } else if (dragging?.kind === 'pawn') {
    // A pawn dragged with no card picked: everywhere some card in hand can take it.
    for (const sq of dropSquares(legal, dragging.pawnId)) {
      const occupant = pawnAtSquare(sq);
      highlights.set(sqKey(sq), occupant && occupant.side !== viewer ? 'capture' : 'move');
    }
    const from = v.pawns[dragging.pawnId]?.square;
    if (from) highlights.set(sqKey(from), 'selected');
  } else if (sel.kind === 'inspect') {
    const sq = v.pawns[sel.pawnId]?.square;
    if (sq) highlights.set(sqKey(sq), 'inspect');
  }

  // The latest move on the board, whole: every link of the turn it belongs to, so a chain can be
  // followed square by square (its squares lit, and a trail in the color of who made it). While
  // this player puts a turn together, that is their turn so far, drafted moves included: it grows
  // with each move and loses one with each Undo, and stays until the next turn's first move.
  const trailLog = useMemo(
    () => (draft ? [...snap.log, ...draft.events.flat()] : snap.log),
    [snap.log, draft],
  );
  let lastMove: readonly Square[] | null = null;
  let lastMover: Side | null = null;
  for (let i = trailLog.length - 1; i >= 0; i--) {
    const e = trailLog[i]!;
    if (e.type === 'LinkPlayed' || e.type === 'EpitaphPlayed') {
      lastMove = lastTurnOf(trailLog, e.side)?.route ?? null;
      lastMover = e.side;
    } else if (e.type !== 'RoundStarted') continue;
    break;
  }

  // ---- Input handlers ------------------------------------------------------------------------
  const pickCard = (cardId: string) => {
    if (!myTurn) return;
    if (sel.kind === 'recover') {
      const a = recoverActions.find((x) => x.type === 'RECOVER' && x.cardId === cardId);
      if (a) commit(a);
      return;
    }
    if (sel.kind === 'pickCard' && sel.cardIds.includes(cardId)) {
      const link = { cardId, pawnId: sel.pawnId, face: null, collateral: null, hops: [] };
      followAim(aimLink(legal, link, sel.goal), sel.goal, link);
      return;
    }
    if (!playableIds.has(cardId)) {
      const p = table.playability(viewer, cardId);
      if (!p.playable)
        say(
          t(`reason.${p.reason}`, { top: v.pile.at(-1) ? faceNotation(v.pile.at(-1)!.face) : '—' }),
        );
      return;
    }
    if (sel.kind === 'link' && sel.link.cardId === cardId) {
      setSel(IDLE);
      return;
    }
    if (v.cards[cardId]?.trick) {
      setSel(IDLE);
      setDialog({ kind: 'trick', cardId });
      return;
    }
    setSel({
      kind: 'link',
      link: { cardId, pawnId: chainPawn, face: null, collateral: null, hops: [] },
    });
  };

  const onSquare = (sq: Square) => {
    // A tap on the board answers the message over it (another may follow from this tap), and puts
    // a pawn held up for a look back down.
    setNotice(null);
    setPeek(null);
    const pawn = pawnAtSquare(sq);
    if (sel.kind === 'link' && stage) {
      const link = stage.selection;
      // Until the first link is committed, clicking another movable pawn switches to it.
      const switchTo =
        !chainPawn && pawn && pawn.id !== link.pawnId && stage.pawnChoices.includes(pawn.id)
          ? pawn.id
          : null;
      const switchPawn = () =>
        switchTo &&
        chooseLink({ ...link, pawnId: switchTo, face: null, collateral: null, hops: [] });
      switch (stage.stage) {
        case 'pawn': {
          if (switchTo) {
            switchPawn();
            return;
          }
          // A destination tapped before a pawn: the pawn that can go there moves.
          const pawns = pawnsForSquare(stage, sq);
          if (pawns.length > 1) {
            say(t('prompt.whichPawn'), pawnQuestionAtTop(pawns));
            return;
          }
          if (pawns.length === 0) return;
          const chosen = linkStage(legal, { ...link, pawnId: pawns[0]! });
          if (chosen.yellow) {
            const done = hopAction(chosen, sq);
            if (done && !hopContinues(chosen, sq)) commit(done);
            else setSel({ kind: 'link', link: { ...chosen.selection, hops: [sq] } });
            return;
          }
          const a = actionForSquare(chosen, sq);
          if (a) commit(a);
          return;
        }
        case 'face':
          switchPawn();
          return;
        case 'collateral':
          if (pawn && stage.collateralChoices.includes(pawn.id))
            chooseLink({ ...link, collateral: pawn.id });
          else if (pawn?.side === viewer && pawn.square && inTheDark(v, pawn.square))
            say(t('hint.inTheDark', { pawn: pawnRef(v, pawn.id, names) }));
          else switchPawn();
          return;
        case 'target':
          // A Wild's collateral, marked on the board: tapping it again (where it is no target)
          // takes the mark off; tapping another pawn that could pay moves the mark there.
          if (
            pawn &&
            link.collateral &&
            stage.collateralChoices.length > 1 &&
            stage.collateralChoices.includes(pawn.id) &&
            !stage.squares.some((s) => sameSquare(s, sq))
          ) {
            chooseLink({ ...link, collateral: pawn.id === link.collateral ? null : pawn.id });
            return;
          }
          if (stage.yellow) {
            if (stage.squares.some((s) => sameSquare(s, sq))) {
              const done = hopAction(stage, sq);
              if (done && !hopContinues(stage, sq)) commit(done);
              else setSel({ kind: 'link', link: { ...link, hops: [...link.hops, sq] } });
            } else switchPawn();
            return;
          }
          {
            const a = actionForSquare(stage, sq);
            if (a) commit(a);
            else switchPawn();
          }
          return;
      }
    }
    if (sel.kind === 'epitaph') {
      const can =
        !!pawn && epitaphActions.some((x) => x.type === 'PLAY_EPITAPH' && x.pawnId === pawn.id);
      // Without hover, a tap plays the replay already shown (tapped once, or held down);
      // another pawn's is shown first. (A tap also focuses the square: that is not showing it.)
      if (can && !pointsFirst && (sel.pawnId ?? peek) !== pawn.id)
        setSel({ ...sel, pawnId: pawn.id });
      else replayEpitaph(pawn?.id);
      return;
    }
    if (pawn) {
      const again = sel.kind === 'inspect' && sel.pawnId === pawn.id;
      setSel(again ? IDLE : { kind: 'inspect', pawnId: pawn.id });
      // Long Night: the pawn's card says what its statuses and Carvings do.
      if (v.ledger) setPeek(again ? null : pawn.id);
      return;
    }
    setSel(IDLE);
    // Long Night: an Omen's square says what it does.
    const omen = v.ledger?.omens.find((o) => !o.spent && sameSquare(o.square, sq));
    if (omen) say(`${omenName(omen.kind)}: ${itemText(`omen.${omen.kind}`)}`);
  };

  const cancel = () => setSel(IDLE);
  /** Plays the Epitaph from this pawn (a Séance Table asks whether it opens a chain). */
  const replayEpitaph = (pawnId: string | undefined) => {
    const options = epitaphActions.filter((x) => x.type === 'PLAY_EPITAPH' && x.pawnId === pawnId);
    const plain = options.find((x) => x.type === 'PLAY_EPITAPH' && !x.chain);
    const chain = options.find((x) => x.type === 'PLAY_EPITAPH' && x.chain);
    if (plain && chain) setDialog({ kind: 'seance', plain, chain });
    else if (plain ?? chain) commit((plain ?? chain)!);
  };
  /**
   * One decision back while a link is being chosen: the last hop, then a Wild's collateral (its
   * mark comes off the board), then its face, then the pawn. A step that had only one choice is
   * passed over, so Back always undoes something the player chose; with nothing left, it cancels.
   */
  const backSteps = (from: LinkSelection): LinkSelection | null => {
    const steps: ((l: LinkSelection) => LinkSelection | null)[] = [
      (l) => (l.hops.length > 0 ? { ...l, hops: l.hops.slice(0, -1) } : null),
      (l) => (l.collateral ? { ...l, collateral: null } : null),
      (l) => (l.face ? { ...l, face: null, collateral: null } : null),
      (l) => (l.pawnId && !chainPawn ? { ...l, pawnId: null, face: null, collateral: null } : null),
    ];
    if (!stage) return null;
    let l = from;
    for (const step of steps) {
      const next = step(l);
      if (!next) continue;
      const again = linkStage(legal, next);
      if (again.stage !== stage.stage || again.selection.hops.length !== from.hops.length)
        return next;
      l = next;
    }
    return null;
  };
  const backTo = stage ? backSteps(stage.selection) : null;
  const back = () => {
    if (backTo) setSel({ kind: 'link', link: backTo });
    else cancel();
  };

  // Pass and Continue are the real match's (the moves of a turn started quietly are the draft's).
  const phaseAction = (type: 'PASS' | 'CONTINUE') => {
    const a = host.legal(viewer).find((x) => x.type === type);
    if (a) submit(a);
  };

  // Keyboard shortcuts (§19.13): digits pick cards; P, C/D, R, T act; Enter or E ends the turn;
  // Backspace or Z takes the last move back; Escape cancels.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (dialog || roundClosing || snap.handoff || e.altKey || e.ctrlKey || e.metaKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')
      )
        return;
      if (document.querySelector('.modal-backdrop')) return;
      const k = e.key.toLowerCase();
      if (k === 'escape') {
        if (peek) setPeek(null);
        else cancel();
        return;
      }
      // I: a look at the Inscription of the pawn on the square in focus.
      if (k === 'i' && focusSq) {
        const pawn = pawnAtSquare(focusSq);
        setPeek(pawn && pawn.id !== peek ? pawn.id : null);
        return;
      }
      if (k === 'backspace' && backTo) {
        e.preventDefault();
        back();
        return;
      }
      if ((k === 'backspace' || k === 'z') && drafting) {
        e.preventDefault();
        takeBack();
        return;
      }
      if ((k === 'enter' || k === 'e') && canEndTurn && sel.kind === 'idle') {
        e.preventDefault();
        endTurn();
        return;
      }
      if (!myTurn) return;
      if (/^[1-9]$/.test(k)) {
        const card = handCards[Number(k) - 1];
        if (card) pickCard(card.card.id);
      } else if (k === 'p' && v.phase === 'TURN_START') phaseAction('PASS');
      else if ((k === 'c' || k === 'd') && v.phase === 'TURN_START') phaseAction('CONTINUE');
      else if (k === 'r' && recoverActions.length > 0) setSel({ kind: 'recover' });
      else if (k === 't' && epitaphActions.length > 0)
        setSel({ kind: 'epitaph', epitaphId: v.players[viewer].epitaphs[0]!.id });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  // ---- Prompt ----------------------------------------------------------------------------------
  const deciderName = snap.controllers[v.decider].name;
  const me = v.players[viewer];
  const canDraw =
    myTurn &&
    v.phase === 'TURN_START' &&
    (me.hand?.length ?? 0) < limitsOf(v, viewer).hand &&
    me.deckCount > 0;
  // Nothing the viewer holds can be played: no card can move and no Epitaph can be replayed.
  // Once a turn has drawn it must still be taken (§6.1), so Recover is then the only way on.
  const nothingToPlay = (() => {
    if (!myTurn || !canAct || drafting || sel.kind !== 'idle') return false;
    if (v.phase === 'ACTION' || quietStart)
      return (
        linkLegal.length + trickLegal.length === 0 &&
        epitaphActions.length === 0 &&
        recoverActions.length > 0
      );
    if (v.phase !== 'TURN_START' || !canDraw) return false;
    const epitaphReady =
      me.epitaphPlays < v.config.constants.EPITAPH_PLAYS_PER_ROUND &&
      table.epitaphOptions(viewer).some((o) => o.check.ok);
    return !epitaphReady && !(me.hand ?? []).some((id) => table.playability(viewer, id).playable);
  })();
  // What the other player did on their latest turn (or is doing), to follow computer and online
  // turns: under the prompt until the viewer starts acting, and beside the board on wide screens.
  const other = opponent(viewer);
  const otherTurn = v.phase === 'MATCH_OVER' ? null : lastTurnOf(snap.log, other);
  const lastTurnText = otherTurn ? describeTurn(otherTurn, snap.controllers[other].name) : null;
  // Under the prompt it follows the other player's turn as it is played, when the prompt is short
  // and there are no buttons; wide screens show it in the side panel instead (styles.css).
  // (A turn started quietly can still pass: its prompt says so, and needs no note.)
  const note = stuckStart
    ? t('note.stuck')
    : nothingToPlay
      ? quietStart
        ? null
        : t(
            v.phase === 'ACTION'
              ? 'note.mustAct'
              : me.deckCount === 1
                ? 'note.nothingMovesLast'
                : 'note.nothingMoves',
          )
      : !myTurn && sel.kind === 'idle' && !drafting
        ? lastTurnText
        : null;
  const noteIsLastTurn = !nothingToPlay && !stuckStart && note !== null;

  const prompt = (() => {
    if (locked) return lockedPrompt ?? '';
    if (v.phase === 'MATCH_OVER') return t('prompt.gameOver');
    // While a score is counted, the turn is still the scorer's: nobody else is shown to play.
    if (burst && countingNow)
      return burst.side === viewer && host.isHuman(viewer)
        ? t('prompt.countingYours')
        : t('prompt.counting', {
            name: snap.controllers[burst.side]?.name ?? names.side(burst.side),
          });
    if (snap.thinking) return t('match.thinking', { name: snap.controllers[snap.thinking].name });
    if (sending) return t('prompt.sending');
    if (!canAct) return t('match.waiting', { name: deciderName });
    if (drafting && !myTurn) return t('prompt.draftDone');
    if (sel.kind === 'recover') return t('prompt.recover');
    if (sel.kind === 'epitaph') return t(sel.pawnId ? 'prompt.epitaphAgain' : 'prompt.epitaph');
    if (sel.kind === 'pickCard') return t('prompt.pickCard');
    if (stage) {
      const card = v.cards[sel.kind === 'link' ? sel.link.cardId : ''];
      switch (stage.stage) {
        case 'pawn':
          return t('prompt.choosePawn', { card: cardLong(card) });
        case 'face':
          return t('prompt.chooseFace');
        case 'collateral': {
          // Pawns on the board are tapped there; reserves (or a Rag Doll) are picked in the list.
          const onBoard = stage.collateralChoices.some((id) => !!v.pawns[id]?.square);
          const offBoard = stage.collateralChoices.some((id) => !v.pawns[id]?.square);
          return t(
            onBoard && offBoard
              ? 'prompt.chooseCollateralEither'
              : offBoard
                ? 'prompt.chooseCollateralReserve'
                : 'prompt.chooseCollateral',
          );
        }
        case 'target':
          return stage.yellow && stage.selection.hops.length > 0
            ? t('prompt.chooseHop')
            : stage.selection.collateral && v.pawns[stage.selection.collateral]?.square
              ? t('prompt.chooseTargetPawning', {
                  pawn: pawnRef(v, stage.selection.pawnId ?? '', names),
                  collateral: pawnRef(v, stage.selection.collateral, names),
                })
              : t('prompt.chooseTarget', { pawn: pawnRef(v, stage.selection.pawnId ?? '', names) });
      }
    }
    switch (v.phase) {
      case 'TURN_START':
        return quietStart
          ? t(nothingToPlay ? 'prompt.turnStartRecover' : 'prompt.turnStartPlay')
          : stuckStart
            ? t('prompt.turnStartStuck')
            : (me.hand?.length ?? 0) >= limitsOf(v, viewer).hand
              ? t('prompt.turnStartFull')
              : me.deckCount === 0
                ? t('prompt.turnStartNoDeck')
                : t('prompt.turnStart');
      case 'ACTION':
        return t(nothingToPlay ? 'prompt.onlyRecover' : 'prompt.action');
      case 'CHAIN':
        if (brazierLit) return t('prompt.brazier');
        return t(drafting ? 'prompt.draftChain' : 'prompt.chain', {
          pawn: pawnRef(v, chainPawn ?? '', names),
        });
      case 'CHOOSE_STARTER':
        return t('prompt.starter', { round: v.round });
      case 'CHOOSE_INSCRIPTION':
        return t('prompt.inscription');
      case 'CHOOSE_EPITAPH':
        return t('prompt.epitaphChoice');
      default:
        return '';
    }
  })();

  const promptRef = useRef<HTMLParagraphElement>(null);
  useFitText(promptRef, prompt);
  const noteRef = useRef<HTMLParagraphElement>(null);
  useFitText(noteRef, note ?? '');

  // ---- Details: the full panels beside the board, one line under the prompt on phones --------
  const inspected = sel.kind === 'inspect' ? v.pawns[sel.pawnId] : undefined;
  const inspectPanel = inspected ? (
    <section className="inspect-panel side-card" aria-live="polite">
      <h3>
        {names.pawn(inspected.id)}{' '}
        {(inspected.name || inspected.standIn) && (
          <span className="dim">({t(`side.${inspected.side}`)})</span>
        )}
      </h3>
      <ul className="preview-lines">
        {inspected.square && <li>{squareName(inspected.square)}</li>}
        {inspected.crowned && <li>{t('pawn.crowned')}</li>}
        {isImmune(v, inspected) && <li>{t('pawn.immune')}</li>}
        {inspected.former && <li>{t('pawn.former')}</li>}
        {marked?.has(inspected.id) && <li className="warn">{t('pawn.marked')}</li>}
      </ul>
      {inspected.inscription ? (
        <div className="route-block">
          <span className="mini-label">{t('inscription.label')}</span>
          <RouteGlyph links={inspected.inscription.links} size={52} flip={flipRoute} />
          <RouteSteps links={inspected.inscription.links} owner={inspected.side} />
          <span className="dim">
            {t('epitaph.score', { score: inspected.inscription.recordedScore })}
          </span>
        </div>
      ) : (
        <p className="dim">{t('inscription.none')}</p>
      )}
    </section>
  ) : null;

  const shownEpitaph =
    sel.kind === 'epitaph'
      ? (epitaphChecks.find((o) => o.pawnId === epitaphPawnId && o.check.ok) ?? null)
      : null;
  const epitaphPanel =
    sel.kind === 'epitaph' ? (
      <section className="epitaph-panel side-card">
        {epitaphChecks.map((o) => {
          if (o.check.ok && epitaphPawnId !== o.pawnId) return null;
          return (
            <EpitaphPreview
              key={o.pawnId}
              v={v}
              check={o.check}
              pawn={o.pawnId}
              names={names}
              flipRoute={flipRoute}
            />
          );
        })}
        {epitaphChecks.every((o) => !o.check.ok) && <p className="dim">{t('epitaph.none')}</p>}
      </section>
    ) : null;

  const info = (() => {
    if (preview) {
      const gift = preview.captures.some((c) => c.epitaph);
      return (
        <>
          <strong>
            {preview.step.notation} · {scoreLine(preview.breakdown)}
          </strong>
          {preview.captures.length > 0 && (
            <span className="good">{t('preview.captures', { n: preview.captures.length })} · </span>
          )}
          {preview.crowned && <span className="good">{t('info.crowns')} · </span>}
          {preview.collateralPawnId && (
            <span className="warn">
              {t('info.collateral', { pawn: names.pawn(preview.collateralPawnId) })} ·{' '}
            </span>
          )}
          {gift && <span className="warn">{t('info.epitaphGift')} · </span>}
          <span className="dim">
            {t('info.nextTop', {
              face: faceNotation(preview.newTop.face, preview.newTop.wasWild),
            })}
          </span>
        </>
      );
    }
    if (shownEpitaph && shownEpitaph.check.ok) {
      const c = shownEpitaph.check;
      return (
        <>
          <strong>
            {t('card.epitaph')} · {scoreLine(c.breakdown)}
          </strong>
          {c.captures.length > 0 && (
            <span className="good">{t('preview.captures', { n: c.captures.length })} · </span>
          )}
          <span className="dim">{t('info.nextTop', { face: faceNotation(c.face, false) })}</span>
        </>
      );
    }
    if (inspected) {
      const bits = [
        `${names.pawn(inspected.id)} · ${inspected.square ? squareName(inspected.square) : ''}`,
        inspected.crowned ? t('pawn.crowned') : '',
        isImmune(v, inspected) ? t('info.immune') : '',
        inspected.inscription
          ? `${inspected.inscription.links.map((l) => faceNotation(l.face, l.wasWild)).join(' → ')} (${inspected.inscription.recordedScore})`
          : '',
      ].filter(Boolean);
      return <span>{bits.join(' · ')}</span>;
    }
    if (draft) {
      // The turn drafted so far: what it scores if it ends here, and the top it leaves.
      const scored = draft.events.flat().findLast((e) => e.type === 'ChainScored');
      const b =
        scored?.type === 'ChainScored' ? scored.breakdown : draft.previews.at(-1)?.breakdown;
      const top = v.pile.at(-1);
      return (
        <>
          {b && (
            <strong>
              {t('info.thisTurn')} · {scoreLine(b)}
            </strong>
          )}
          {top && (
            <span className="dim">
              {t('info.nextTop', {
                face: faceNotation(top.face, top.source === 'card' && top.wasWild),
              })}
            </span>
          )}
        </>
      );
    }
    return null;
  })();

  // The drafted turn's summary is not shown beside the board: it stays under the prompt.
  const infoIsDraft = !preview && !shownEpitaph?.check.ok && !inspected && !!draft;
  const infoRef = useRef<HTMLParagraphElement>(null);
  useFitText(
    infoRef,
    `${preview?.step.notation ?? ''}|${shownEpitaph?.pawnId ?? ''}|${inspected?.id ?? ''}|${
      infoIsDraft ? turn.step : ''
    }`,
  );

  // On a phone the picker is a sheet over the prompt and its buttons, so it carries both. Beside
  // the pile on a desktop the prompt's own show instead, and the sheet's are hidden (styles.css).
  const pickerActions = (
    <div className="picker-actions">
      {myTurn && backTo && (
        <button type="button" className="btn btn-small" onClick={back}>
          {t('action.back')}
        </button>
      )}
      <button type="button" className="btn btn-small" onClick={cancel}>
        {t('action.cancel')}
      </button>
    </div>
  );
  const wildPicker =
    stage && stage.stage === 'face' ? (
      <div className="picker face-picker" role="group" aria-label={t('wild.pickFace')}>
        <p className="picker-title">{prompt}</p>
        {(['R', 'B', 'G', 'Y'] as const).map((color) => {
          const faces = ALL_FACES.filter(
            (f) => f.color === color && stage.faceChoices.some((x) => sameFace(x, f)),
          );
          if (faces.length === 0) return null;
          return (
            <div key={color} className={`face-row c-${color}`}>
              {faces.map((f: Face) => (
                <button
                  key={`${f.color}${f.number}`}
                  type="button"
                  className="btn btn-face"
                  aria-label={t('card.wildNamed', { face: `${t(`color.${f.color}`)} ${f.number}` })}
                  onClick={() =>
                    sel.kind === 'link' &&
                    chooseLink({ ...stage.selection, face: f, collateral: null })
                  }
                >
                  <Glyph color={f.color} size={14} /> {f.number}
                </button>
              ))}
            </div>
          );
        })}
        {pickerActions}
      </div>
    ) : stage &&
      stage.stage === 'collateral' &&
      stage.collateralChoices.some((id) => !v.pawns[id]?.square) ? (
      // Pawns on the board are chosen on the board; only those off it (reserves, a Rag Doll) here.
      <div className="picker collateral-picker" role="group" aria-label={t('wild.pickCollateral')}>
        <p className="picker-title">{prompt}</p>
        {stage.collateralChoices
          .filter((id) => !v.pawns[id]?.square)
          .map((id) => {
            const p = v.pawns[id];
            // A pawn on the board by its square (and its name, when it has one), kept short so the
            // sheet stays small; its full name is read out. A Rag Doll pays for nothing (Long Night).
            const label =
              id === FREE_COLLATERAL
                ? t('wild.free')
                : p?.square
                  ? p.name
                    ? `${p.name} · ${squareName(p.square)}`
                    : squareName(p.square)
                  : t('wild.reserve', { name: names.pawn(id) });
            return (
              <button
                key={id}
                type="button"
                className="btn btn-small"
                aria-label={
                  p?.square
                    ? t('wild.collateralOn', { pawn: names.pawn(id), square: squareName(p.square) })
                    : undefined
                }
                onClick={() => chooseLink({ ...stage.selection, collateral: id })}
              >
                {label}
              </button>
            );
          })}
        {pickerActions}
      </div>
    ) : null;

  const actions = (
    <div className="prompt-actions">
      {myTurn && v.phase === 'TURN_START' && (
        <>
          <button type="button" className="btn" onClick={() => phaseAction('PASS')} title="P">
            {t('action.pass')}
          </button>
          {!canDraw && !quietStart && !stuckStart && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => phaseAction('CONTINUE')}
              title="C"
            >
              {t('action.continue')}
            </button>
          )}
        </>
      )}
      {drafting && !sending && sel.kind !== 'link' && (
        <button
          type="button"
          className="btn btn-undo"
          onClick={takeBack}
          title={`${t('action.undo')} (Z)`}
          aria-label={t('action.undoLong')}
        >
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="M8 4 3.5 8.5 8 13M4 8.5h7.5a5 5 0 0 1 0 10H9" />
          </svg>
          {t('action.undo')}
        </button>
      )}
      {canEndTurn && sel.kind === 'idle' && (
        <button
          type="button"
          className="btn btn-primary btn-end"
          onClick={() => endTurn()}
          title={`${t('action.endTurn')} (Enter)`}
        >
          {t('action.endTurn')}
        </button>
      )}
      {acting && v.phase !== 'CHAIN' && sel.kind === 'idle' && epitaphActions.length > 0 && (
        <button
          type="button"
          className="btn"
          title={`${t('action.playEpitaph')} (T)`}
          aria-label={t('action.playEpitaph')}
          onClick={() => setSel({ kind: 'epitaph', epitaphId: v.players[viewer].epitaphs[0]!.id })}
        >
          {t('action.playEpitaphShort')}
        </button>
      )}
      {acting && v.phase !== 'CHAIN' && sel.kind === 'idle' && recoverActions.length > 0 && (
        <button
          type="button"
          className={`btn ${nothingToPlay ? 'btn-primary' : ''}`}
          title="R"
          onClick={() => setSel({ kind: 'recover' })}
        >
          {t('action.recover')}
        </button>
      )}
      {stage?.stopHere && (
        <button type="button" className="btn btn-primary" onClick={() => commit(stage.stopHere!)}>
          {t('action.stopHere')}
        </button>
      )}
      {myTurn && sel.kind === 'epitaph' && sel.pawnId && (
        <button type="button" className="btn btn-primary" onClick={() => replayEpitaph(sel.pawnId)}>
          {t('action.replay')}
        </button>
      )}
      {myTurn && backTo && (
        <button type="button" className="btn" onClick={back} title="Backspace">
          {t('action.back')}
        </button>
      )}
      {myTurn && sel.kind !== 'idle' && sel.kind !== 'inspect' && (
        <button type="button" className="btn" onClick={cancel} title="Esc">
          {t('action.cancel')}
        </button>
      )}
    </div>
  );
  const talkRef = useRef<HTMLDivElement>(null);
  useFitTalk(
    talkRef,
    `${prompt}|${note ?? ''}|${typeof info === 'string' ? info : !!info}|${v.phase}|${sel.kind}|${
      drafting
    }|${canEndTurn}|${!!wildPicker}`,
  );

  const opp = opponent(orientation);
  const result = v.result;
  const iWon = result ? (humanCount === 2 ? null : result.winner === viewer) : null;
  const winners = v.rounds.map((r) => r.winner);
  // The scores beside the board are the match's own, never a drafted turn's: a turn's score is
  // counted when the turn is played (ScoreBurst), and the scorer's score rolls up to it only once
  // that count is done.
  const roundShown = (side: Side): number | undefined =>
    burst && !burst.landed && burst.side === side
      ? burst.held
      : draft
        ? snap.view.players[side].roundScore
        : undefined;
  const strip = (side: Side, className: string) => (
    <PlayerStrip
      className={className}
      v={v}
      side={side}
      controller={snap.controllers[side]}
      isViewer={viewer === side}
      showCounts={!(viewer === side && handShown)}
      decider={
        snap.view.phase === 'MATCH_OVER'
          ? null
          : burst && countingNow
            ? burst.side
            : snap.view.decider
      }
      thinking={snap.thinking === side}
      heldRound={roundShown(side)}
      reduceMotion={reduceMotion}
      onOpen={(s) => setDialog({ kind: 'player', side: s })}
      kit={
        v.ledger ? (
          <StripKit
            v={v}
            side={side}
            mine={side === viewer}
            glass={side !== viewer && !!nightTally?.glass}
            curioReady={(item) =>
              curioLegal.some((a) => curioIdOf(a.uid) === item.id) && curioHint(item) === null
            }
            onItem={(item) => {
              // Its sheet steps through the side's whole kit, Mantel then Curios.
              const kit = kitItems(v, side);
              setDialog({
                kind: 'nightItem',
                items: kit.map((k) => k.show),
                at: Math.max(
                  0,
                  kit.findIndex((k) => k.uid === item.uid),
                ),
              });
            }}
            onCurio={(item) => setDialog({ kind: 'curio', item })}
          />
        ) : undefined
      }
      ante={
        v.ledger && side === 'N' && v.config.northHeadStart > 0 ? (
          <Ante key={v.round} n={v.config.northHeadStart} still={reduceMotion} />
        ) : undefined
      }
    />
  );
  const lastTurnPanel = lastTurnText ? (
    <section className="side-card last-turn-panel" aria-label={t('lastTurn.title')}>
      <h3>{t('lastTurn.title')}</h3>
      <p>{lastTurnText}</p>
    </section>
  ) : null;
  const sideContent = preview ? (
    <PreviewPanel v={v} preview={preview} names={names} flipRoute={flipRoute} />
  ) : (
    (epitaphPanel ?? inspectPanel ?? lastTurnPanel)
  );

  return (
    <div
      ref={rootRef}
      className={`match ${settings.highContrast ? 'high-contrast' : ''} ${coach ? 'has-coach' : ''} ${
        wildPicker ? 'has-picker' : ''
      }`}
      data-horror={horrorRule}
    >
      {horrorRule && !reduceMotion && <HorrorAmbience horror={horrorRule} />}
      {horrors.frame?.screen}
      <div className="match-inner" inert={!!snap.handoff && !result}>
        <header className={`match-bar ${nightTally ? 'is-night' : ''}`}>
          <h1 className="sr-only">{title ?? t('app.title')}</h1>
          <RoundDots round={v.round} max={v.config.constants.MAX_ROUNDS} winners={winners} />
          {nightTally?.ink && (
            // Closing Time: his eyes, above the lamp; they close only if he loses.
            <BrokerEyes
              open={!(result && result.winner === viewer)}
              look={pointerSq ? (pointerSq.x - 2.5) / 2.5 : 0}
              still={reduceMotion}
              small
            />
          )}
          {title && (
            <strong className={`match-title ${nightTally ? 'is-night' : ''}`} aria-hidden="true">
              {nightTally && <Candle night={nightTally.night} size={28} />}
              {title}
            </strong>
          )}
          <div className="bar-actions">
            {v.ledger && (
              <LedgerPill
                v={v}
                side={viewer}
                curioReady={curioLegal.some((a) => curioHint(curioItemOf(a.uid)) === null)}
                onOpen={() => setDialog({ kind: 'ledger' })}
              />
            )}
            {ledgerTip && (
              // Once, at the first Long Night table: what the coin is, pointing at it.
              <p className="ln-coach" role="status">
                <span>{t('ledger.tableTip')}</span>
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => {
                    markGuideSeen('night-table');
                    setLedgerTipClosed(true);
                  }}
                >
                  {t('campaign.ticketTipDismiss')}
                </button>
              </p>
            )}
            <button
              type="button"
              className="icon-btn"
              aria-label={t('bar.log')}
              title={t('bar.log')}
              onClick={() => setDialog({ kind: 'log' })}
            >
              <Icon name="log" />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={t('match.menu')}
              title={t('match.menu')}
              onClick={() => setDialog({ kind: 'menu' })}
            >
              <Icon name="more" />
            </button>
          </div>
        </header>

        <div className="match-body">
          {coach && <div className="area-coach">{coach}</div>}
          {strip(opp, 'area-opp')}

          <main
            className="area-board"
            onPointerDown={onBoardPointerDown}
            onContextMenu={onBoardContextMenu}
          >
            <h2 className="sr-only">{t('match.board')}</h2>
            <Board
              view={v}
              viewer={orientation}
              names={names}
              highlights={highlights}
              ghost={ghost}
              ghostKind={ghostKind}
              lastMove={lastMove}
              lastMover={lastMover}
              marked={marked}
              lastEvents={boardEvents}
              eventsKey={boardKey}
              undone={!echo && !!turn.undone}
              onSquare={onSquare}
              draggable={draggableSquares}
              lifted={dragging?.kind === 'pawn' ? dragging.pawnId : null}
              pawning={
                stage && stage.stage === 'target' && stage.selection.collateral
                  ? stage.selection.collateral
                  : null
              }
              heldAt={horrors.frame?.heldAt ?? null}
              lifting={horrors.frame?.lifting}
              stage={horrors.frame?.stage}
              inner={horrors.frame?.inner}
              outer={horrors.frame?.outer}
              dark={v.ledger ? darkFiles(v) : null}
              onHover={onPointerSquare}
              onFocusSquare={onFocusSquare}
              reduceMotion={reduceMotion}
              label={t('app.title')}
              marks={nightTally?.marks}
              arriving={!!nightTally?.ink && !reduceMotion && v.round === 1 && snap.actionCount < 2}
              frame={
                v.config.ledger && v.config.ledger.houseRules.length > 0 ? (
                  <span className="board-placard">
                    <Placard
                      rules={v.config.ledger.houseRules}
                      emblem={nightTally?.prop}
                      clock={(r) => ruleClock(r, v, names)}
                      onOpen={() => setDialog({ kind: 'ledger' })}
                    />
                  </span>
                ) : undefined
              }
            />
            {burst && v.ledger && burst.breakdown.steps && (
              <TallyView
                key={burst.key}
                breakdown={burst.breakdown}
                who={snap.controllers[burst.side]?.name ?? names.side(burst.side)}
                source={burst.source}
                pawnId={burst.pawnId}
                half={burst.side === orientation ? 'bottom' : 'top'}
                pace={settings.aiSpeed}
                reduceMotion={reduceMotion}
                ink={nightTally?.ink === burst.side}
                tint={burst.side === 'S' ? undefined : nightTally?.tint}
                flare={burst.flare}
                record={burst.record}
                level={
                  burst.breakdown.multiplier.pattern
                    ? (v.config.ledger?.kits[burst.side].appraisals[
                        burst.breakdown.multiplier.pattern as AppraisalTarget
                      ] ?? 1)
                    : 1
                }
                cards={v.cards}
                onLanded={() => setBurst((b) => b && { ...b, landed: true })}
                onDone={() => setBurst(null)}
              />
            )}
            {burst && !(v.ledger && burst.breakdown.steps) && (
              <ScoreBurst
                key={burst.key}
                breakdown={burst.breakdown}
                who={names.side(burst.side)}
                source={burst.source}
                pace={settings.aiSpeed}
                reduceMotion={reduceMotion}
                onTick={settings.sound ? () => sounds.play('tick') : undefined}
                onLanded={() => setBurst((b) => b && { ...b, landed: true })}
                onDone={() => setBurst(null)}
              />
            )}
            {peek && (
              <PawnPeek
                v={v}
                pawnId={peek}
                viewer={viewer}
                names={names}
                flipRoute={flipRoute}
                replay={
                  sel.kind === 'epitaph'
                    ? (epitaphChecks.find((o) => o.pawnId === peek)?.check ?? null)
                    : null
                }
                place={peekPlace}
                onClose={() => setPeek(null)}
              />
            )}
            {(banner || warning || notice?.top) && (
              <div className="toasts toasts-top">
                {banner}
                {warning && (
                  <Toast error onClose={() => setWarningClosed(true)}>
                    {warning}
                  </Toast>
                )}
                {notice?.top && <Toast onClose={() => say(null)}>{notice.text}</Toast>}
              </div>
            )}
            {((notice && !notice.top) || ticketTip || snap.error) && (
              <div className="toasts toasts-bottom">
                {notice && !notice.top && <Toast onClose={() => say(null)}>{notice.text}</Toast>}
                {ticketTip && (
                  <p className="notice ticket-tip" role="status">
                    <span className="ticket-inline" aria-hidden="true" />
                    <span>{t('campaign.ticketTip')}</span>
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => setTicketTipClosed(true)}
                    >
                      {t('campaign.ticketTipDismiss')}
                    </button>
                  </p>
                )}
                {snap.error && (
                  <p className="notice notice-error" role="alert">
                    {t('error.generic', { message: snap.error })}
                  </p>
                )}
              </div>
            )}
          </main>

          {strip(orientation, 'area-me')}

          <section
            className={`area-table ${myTurn ? 'is-mine' : ''}`}
            aria-label={t('match.table')}
          >
            <div className="table-cards">
              <DeckView
                side={viewer}
                count={me.deckCount}
                canDraw={canDraw}
                onDraw={() => phaseAction('CONTINUE')}
              />
              <PileView v={v} onShowPile={() => setDialog({ kind: 'pile' })} />
            </div>
            <div ref={talkRef} className="table-talk">
              <p ref={promptRef} className={`prompt-text ${info ? 'has-info' : ''}`} role="status">
                {prompt}
              </p>
              {info ? (
                <p
                  ref={infoRef}
                  className={`talk-info ${infoIsDraft ? 'is-draft' : ''}`}
                  aria-live="polite"
                >
                  {info}
                </p>
              ) : (
                note && (
                  <p
                    ref={noteRef}
                    className={`talk-info is-note ${noteIsLastTurn ? 'is-last-turn' : ''}`}
                  >
                    {note}
                  </p>
                )
              )}
              {wildPicker}
              {actions}
            </div>
          </section>

          <section
            className="area-hand"
            aria-label={t('match.hand')}
            onPointerDown={onHandPointerDown}
          >
            {snap.handoff || !handShown ? (
              <p className="hand-empty dim">
                {t('match.cardsHidden', { n: v.players[viewer].handCount })}
              </p>
            ) : (
              <HandView
                v={v}
                cards={handCards}
                selectedId={sel.kind === 'link' ? sel.link.cardId : null}
                mode={
                  sel.kind === 'recover'
                    ? 'recover'
                    : acting
                      ? 'play'
                      : turnSpent
                        ? 'spent'
                        : 'idle'
                }
                interactive={acting}
                burning={acting && brazierLit}
                candidates={sel.kind === 'pickCard' ? sel.cardIds : undefined}
                lifted={dragging?.kind === 'card' ? dragging.cardId : null}
                onPick={pickCard}
              />
            )}
          </section>

          <aside className="area-side" aria-label={t('match.details')}>
            {sideContent}
          </aside>
        </div>
      </div>

      <LogAnnouncer events={snap.log} names={names} />
      <div ref={layerRef} className="flight-layer" aria-hidden="true" />

      {/* Decisions that need a dialog (§19.9). */}
      {myTurn && !roundClosing && v.phase === 'CHOOSE_STARTER' && (
        <StarterDialog
          round={v.round}
          opponentName={snap.controllers[opponent(viewer)].name}
          onChoose={(meFirst) =>
            submit({
              type: 'CHOOSE_STARTER',
              side: viewer,
              starter: meFirst ? viewer : opponent(viewer),
            })
          }
        />
      )}
      {myTurn &&
        !roundClosing &&
        v.phase === 'CHOOSE_INSCRIPTION' &&
        v.pendingInscription &&
        settings.inscriptions === 'ask' && (
          <InscriptionDialog
            current={v.pawns[v.pendingInscription.pawnId]!.inscription!}
            next={v.pendingInscription.inscription}
            pawnName={pawnRef(v, v.pendingInscription.pawnId, names)}
            owner={viewer}
            flip={flipRoute}
            onChoose={(replace) => submit({ type: 'CHOOSE_INSCRIPTION', side: viewer, replace })}
          />
        )}
      {epitaphChoiceShown && v.pendingEpitaphChoice && (
        <EpitaphChoiceDialog
          candidates={v.pendingEpitaphChoice.candidates.filter(
            (e) => !v.pendingEpitaphChoice!.kept.includes(e.id),
          )}
          held={v.players[viewer].epitaphs.map((e) => e.id)}
          capturedOn={(pawnId) => {
            for (let i = snap.log.length - 1; i >= 0; i--) {
              const e = snap.log[i]!;
              if (e.type === 'PawnCaptured' && e.pawnId === pawnId) return e.square;
            }
            return null;
          }}
          opponentName={snap.controllers[opponent(viewer)].name}
          constants={v.config.constants}
          names={names}
          onKeep={(id) => submit({ type: 'CHOOSE_EPITAPH', side: viewer, keepEpitaphId: id })}
        />
      )}

      {dialog?.kind === 'pile' && (
        <PileDialog v={v} names={names} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'player' && (
        <PlayerDialog
          v={v}
          side={dialog.side}
          names={names}
          flipRoute={flipRoute}
          marked={marked}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'confirm' && (
        <ConfirmDialog
          title={t('dialog.confirm.title')}
          body={dialog.text}
          confirmLabel={t('action.confirm')}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            const a = dialog.action;
            setDialog(null);
            playNow(a);
          }}
        />
      )}
      {dialog?.kind === 'ledger' && (
        <LedgerDialog
          v={v}
          viewer={viewer}
          names={names}
          sideName={(side) => snap.controllers?.[side]?.name ?? t(`side.${side}`)}
          curioActions={curioLegal}
          curioHint={curioHint}
          onCurio={(item) => setDialog({ kind: 'curio', item })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'nightItem' && dialog.items[dialog.at] && (
        <ItemSheet
          item={dialog.items[dialog.at]!}
          actions={[]}
          browse={
            dialog.items.length > 1
              ? {
                  at: dialog.at,
                  of: dialog.items.length,
                  onStep: (d) =>
                    setDialog({
                      ...dialog,
                      at: (dialog.at + d + dialog.items.length) % dialog.items.length,
                    }),
                }
              : undefined
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'curio' && (
        <CurioDialog
          v={v}
          names={names}
          item={dialog.item}
          actions={curioLegal.filter((a) => curioIdOf(a.uid) === dialog.item.id)}
          onPick={(a) => {
            setDialog(null);
            commit(a);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'trick' && (
        <TrickDialog
          v={v}
          names={names}
          cardId={dialog.cardId}
          actions={trickLegal.filter((a) => a.cardId === dialog.cardId)}
          chainPawn={chainPawn}
          onPick={(a) => {
            setDialog(null);
            commit(a);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'seance' && (
        <Modal
          title={itemName('seance-table')}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  const a = dialog.plain;
                  setDialog(null);
                  commit(a);
                }}
              >
                {t('ledger.seance.replay')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-autofocus
                onClick={() => {
                  const a = dialog.chain;
                  setDialog(null);
                  commit(a);
                }}
              >
                {t('ledger.seance.chain')}
              </button>
            </>
          }
        >
          <p>{itemText('seance-table')}</p>
        </Modal>
      )}
      {dialog?.kind === 'endTurn' && (
        <ConfirmDialog
          title={t('action.endTurn')}
          body={t('dialog.confirm.endTurn')}
          confirmLabel={t('action.endTurn')}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            setDialog(null);
            endTurn(true);
          }}
        />
      )}
      {dialog?.kind === 'describe' && (
        <Modal title={t('match.describe')} onClose={() => setDialog(null)} wide>
          {describePosition(v, names).map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </Modal>
      )}
      {dialog?.kind === 'symbols' && (
        <Modal title={t('legend.title')} onClose={() => setDialog(null)} wide>
          <SymbolLegend />
        </Modal>
      )}
      {dialog?.kind === 'log' && (
        <Modal title={t('match.log')} onClose={() => setDialog(null)} className="modal-log">
          <LogPanel events={snap.log} names={names} announce={false} heading={false} />
        </Modal>
      )}
      {dialog?.kind === 'menu' && (
        <Modal title={t('match.menu')} onClose={() => setDialog(null)} className="modal-menu">
          <div className="menu-list">
            <button
              type="button"
              className="menu-item"
              onClick={() => setDialog({ kind: 'symbols' })}
            >
              {t('legend.button')}
              <span className="menu-hint">{t('menu.symbolsHint')}</span>
            </button>
            <button type="button" className="menu-item" onClick={() => setDialog({ kind: 'log' })}>
              {t('match.log')}
              <span className="menu-hint">{t('menu.logHint')}</span>
            </button>
            <button
              type="button"
              className="menu-item"
              onClick={() => setDialog({ kind: 'describe' })}
            >
              {t('match.describe')}
              <span className="menu-hint">{t('menu.describeHint')}</span>
            </button>
            <button
              type="button"
              className="menu-item"
              aria-pressed={flipped}
              onClick={() => {
                setFlipped((f) => !f);
                setDialog(null);
              }}
            >
              {t('match.flip')}
              <span className="menu-hint">{t('menu.flipHint')}</span>
            </button>
            {canGoFullscreen() && (
              <button
                type="button"
                className="menu-item"
                aria-pressed={isFullscreen()}
                onClick={() => {
                  void toggleFullscreen();
                  setDialog(null);
                }}
              >
                {isFullscreen() ? t('app.leaveFullscreen') : t('app.fullscreen')}
                <span className="menu-hint">{t('app.fullscreenHint')}</span>
              </button>
            )}
            {onShare && (
              <div className="menu-share">
                <ShareButton create={onShare} label={t('share.match')} />
              </div>
            )}
            {onExport && (
              <button type="button" className="menu-item" onClick={onExport}>
                {t('dialog.matchEnd.export')}
              </button>
            )}
            {onSaveAndExit && v.phase !== 'MATCH_OVER' && (
              <button type="button" className="menu-item" onClick={onSaveAndExit}>
                {t('match.saveExit')}
              </button>
            )}
            <button type="button" className="menu-item" onClick={onExit}>
              {t('match.exit')}
            </button>
            {canConcede && v.phase !== 'MATCH_OVER' && humanCount > 0 && (
              <button
                type="button"
                className="menu-item menu-danger"
                onClick={() => setDialog({ kind: 'concede' })}
              >
                {t('match.concede')}
              </button>
            )}
          </div>
          <p className="menu-rules dim">
            {t('menu.rulesPreset', {
              preset:
                v.config.preset === 'CUSTOM'
                  ? t('menu.customRules')
                  : t(`setup.preset.${v.config.preset}`),
            })}
          </p>
        </Modal>
      )}
      {dialog?.kind === 'concede' && (
        <ConfirmDialog
          title={t('match.concede')}
          body={t('match.concedeConfirm', {
            opponent: snap.controllers[opponent(host.isHuman(viewer) ? viewer : 'S')].name,
          })}
          confirmLabel={t('match.concede')}
          danger
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            setDialog(null);
            const side = host.isHuman(viewer) ? viewer : v.decider;
            submit({ type: 'CONCEDE', side });
          }}
        />
      )}
      {noMove && !burst && (
        <NoMoveDialog
          noMove={noMove}
          names={names}
          onClose={() => {
            setNoMove(null);
            // It told the round's result: no second dialog for it (the Long Night's interlude
            // still plays, as the beat between rounds).
            if (noMove.roundOver !== null && !nightTally) setRoundDialog(null);
          }}
        />
      )}
      {itemMoment && !burst && (
        <ItemMoment
          key={itemMoment.key}
          reason={itemMoment.reason}
          id={itemMoment.id}
          still={reduceMotion}
          onDone={() => setItemMoment(null)}
        />
      )}
      {roundDialog && !result && !burst && !noMove && nightTally && (
        <Intermission
          next={roundDialog.round + 1}
          result={
            roundDialog.winner
              ? t('dialog.roundEnd.winner', {
                  name: names.side(roundDialog.winner),
                  w: roundDialog.scores[roundDialog.winner],
                  l: roundDialog.scores[opponent(roundDialog.winner)],
                })
              : t('dialog.roundEnd.tie', { s: roundDialog.scores.S })
          }
          still={reduceMotion}
          onDone={() => setRoundDialog(null)}
        />
      )}
      {roundDialog && !result && !burst && !noMove && !nightTally && (
        <RoundEndDialog round={roundDialog} names={names} onClose={() => setRoundDialog(null)} />
      )}
      {result && !burst && !noMove && (
        <MatchEndDialog
          result={result}
          rounds={v.rounds}
          totals={{ S: v.players.S.totalScore, N: v.players.N.totalScore }}
          names={names}
          viewerWon={iWon}
          onSaveReplay={onSaveReplay}
          onExport={onExport}
          onRematch={onRematch}
          onExit={onExit}
          exitLabel={exitLabel}
          onShare={onShare}
          extra={endExtra}
        />
      )}
      {snap.handoff && !result && !roundClosing && (
        <HandoffOverlay
          side={snap.handoff}
          name={snap.controllers[snap.handoff].name}
          onReady={() => host.acknowledgeHandoff()}
        />
      )}
      {drag.active && (
        <DragCopy
          ghostRef={drag.ghostRef}
          width={drag.active.width}
          height={drag.active.height}
          v={v}
          source={drag.active.source}
          flipRoute={orientation === 'N'}
        />
      )}
    </div>
  );
}

/** What is being dragged, drawn under the pointer at the size it had where it was picked up. */
function DragCopy({
  ghostRef,
  width,
  height,
  v,
  source,
  flipRoute,
}: {
  ghostRef: (el: HTMLElement | null) => void;
  width: number;
  height: number;
  v: MatchView;
  source: DragSource;
  flipRoute: boolean;
}) {
  const pawn = source.kind === 'pawn' ? v.pawns[source.pawnId] : undefined;
  const card = source.kind === 'card' ? v.cards[source.cardId] : undefined;
  return (
    <div
      ref={ghostRef}
      className={`drag-copy drag-${source.kind}`}
      style={{ width, height, '--copy-w': `${width}px` } as CSSProperties}
      aria-hidden="true"
    >
      {pawn && (
        <PawnFigure
          side={pawn.side}
          crowned={pawn.crowned}
          immune={false}
          inscription={pawn.inscription}
          flipRoute={flipRoute}
        />
      )}
      {card && (
        <CardView
          face={card.face ?? null}
          wild={card.kind === 'wild'}
          size="md"
          state="selected"
          label=""
        />
      )}
    </div>
  );
}
